import React, { useMemo, useCallback } from 'react';
import { Button, Space, Card, Grid } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from 'react-i18next';
import SharedFilterBar from '../../Shared/Filters/SharedFilterBar';
import { credentialFilterFields } from '../../Shared/Filters/filterConfig';
import useFilterOptionFields, { FILTER_OPTION_RESOURCE } from '../../Shared/Filters/useFilterOptionFields';
import AppTable from '../../Shared/AppTable/AppTable';
import { TABLE_CLASS, getColumns } from './constants';
import SkeletonTable from './SkeletonTable';
import PageToolbar from '../../Shared/PageToolbar';

const { useBreakpoint } = Grid;

const CREDENTIAL_FILTER_OPTION_RESOURCES = {
    user_id: FILTER_OPTION_RESOURCE.USER,
    channel_id: FILTER_OPTION_RESOURCE.CHANNEL
};

const CredentialTable = ({
    credentials,
    loading,
    isInitialLoading,
    pagination,
    searchString,
    filters,
    sorter,
    onTableChange,
    onSearch,
    onFilterChange,
    onAction,
    onAdd,
    getHighlightClass,
    softRefreshKey,
    deleteLoading,
}) => {
    const { t } = useTranslation();
    const screens = useBreakpoint() || {};
    const filterFields = useFilterOptionFields(credentialFilterFields, CREDENTIAL_FILTER_OPTION_RESOURCES);

    const columns = useMemo(() => getColumns(t, screens, onAction, deleteLoading), [t, screens, onAction, deleteLoading]);

    const handleSearch = useCallback((e) => {
        onSearch(e.target.value);
    }, [onSearch]);

    const handleClearSearch = useCallback(() => {
        onSearch('');
    }, [onSearch]);

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <PageToolbar
                title={t('credentials')}
                actions={
                    <Button
                        type="link"
                        icon={<PlusOutlined />}
                        onClick={onAdd}
                    >
                        {!screens.xs && t('add_credential')}
                    </Button>
                }
                search={{
                    placeholder: t('search'),
                    value: searchString,
                    onChange: handleSearch,
                    allowClear: true,
                    onClear: handleClearSearch,
                    desktopWidth: 300,
                }}
                filters={
                    <SharedFilterBar
                        fields={filterFields}
                        value={filters}
                        onChange={onFilterChange}
                        sortFields={[
                            { value: 'channel_type', labelKey: 'filters_field_channel_type' },
                            { value: 'name', labelKey: 'name' },
                            { value: 'first_name', labelKey: 'first_name' },
                            { value: 'last_name', labelKey: 'last_name' },
                            { value: 'email', labelKey: 'email' },
                            { value: 'credential_user_id', labelKey: 'credential_user_id' },
                            { value: 'token_expires_at', labelKey: 'token_expires_at' },
                            { value: 'is_permanent_token', labelKey: 'filters_field_permanent_token' },
                            { value: 'created_at', labelKey: 'created_at' },
                            { value: 'updated_at', labelKey: 'updated_at' },
                        ]}
                        sortValue={{ sort_by: sorter.field, sort_direction: sorter.order }}
                        onSortChange={(sort) => {
                            onTableChange({ ...pagination, current: 1 }, filters, { field: sort.sort_by, order: sort.sort_direction === 'asc' ? 'ascend' : (sort.sort_direction === 'desc' ? 'descend' : undefined) });
                        }}
                    />
                }
            />

            <Card style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                {isInitialLoading ? (
                    <SkeletonTable columns={columns} rowCount={10} active={true} />
                ) : (
                    <AppTable
                        key={softRefreshKey}
                        className={TABLE_CLASS}
                        columns={columns}
                        dataSource={credentials}
                        rowKey="id"
                        scroll={{ x: 800 }}
                        loading={loading}
                        pagination={{
                            ...pagination,
                            showSizeChanger: !screens.xs,
                        }}
                        onChange={onTableChange}
                        rowClassName={getHighlightClass ? (record) => getHighlightClass(record.id) : undefined}
                    />
                )}
            </Card>
        </Space>
    );
};

export default CredentialTable;
